<template>
  <div>
    <div v-if="machine?.openReviews.count > 0" class="float-right">
      <open-reviews-notification :machineSerial="machineSerial" identifier="machineFileEditNotifications"/>
    </div>
    <h1 class="page-header">Machine File {{ machineSerial.toUpperCase() }} </h1>
    <b-alert v-if="saveErrorText" show dismissible variant="danger" class="mb-3" @dismissed="saveErrorText = null">
      {{ saveErrorText }}
    </b-alert>
    <div v-if="machineFile" class="row">
      <div class="col-md-6">
        <static-data :data="machineFile" :editable="true" @onChange="applyChanges"></static-data>
        <weight-data :data="machineFile" :editable="true" @onChange="applyChanges"></weight-data>
        <application-data :data="machineFile" :editable="true" @onChange="applyChanges"></application-data>
        <media-data :data="machineFile" :editable="true" @onChange="applyChanges"></media-data>
      </div>
      <div class="col-md-6">
        <suspension-data :data="machineFile" :editable="true" @onChange="applyChanges"></suspension-data>
        <lubrication-data :data="machineFile" :editable="true" @onChange="applyChanges"></lubrication-data>
        <motor-data :data="machineFile" :editable="true" @onChange="applyChanges"></motor-data>
        <revision-data :data="machineFile" :editable="true" @onChange="applyChanges"></revision-data>
      </div>
    </div>
    <div v-else class="text-center">
      <span v-if="errorText">{{ errorText }}</span>
      <b-spinner v-else label="Loading..." show variant="secondary"></b-spinner>
    </div>
    <div class="d-flex justify-content-end align-items-center mb-5" v-if="userHasRole && machineFile">
      <span v-if="saveErrorText" class="text-danger mr-3 font-weight-bold">{{ saveErrorText }}</span>
      <b-button @click="saveChanges" size="sm" variant="outline-dark" :disabled="isSaving">
        <b-spinner small v-if="isSaving" class="mr-1"></b-spinner>
        <font-awesome-icon v-else icon="check" class="mr-1"/>
        Save
      </b-button>
    </div>
    <b-toast ref="successToast" variant="success" fade no-title no-close-button auto-hide-delay="3000">
      Machine File saved successfully.
    </b-toast>
  </div>
</template>

<script>
import staticData from '@/components/machine_file/StaticData.vue'
import weightData from '@/components/machine_file/WeightData.vue'
import applicationData from '@/components/machine_file/ApplicationData.vue'
import mediaData from '@/components/machine_file/MediaData.vue'
import suspensionData from '@/components/machine_file/SuspensionData.vue'
import lubricationData from '@/components/machine_file/LubricationData.vue'
import motorData from '@/components/machine_file/MotorData.vue'
import revisionData from '@/components/machine_file/RevisionData.vue'
import { machinesApi } from '@/backend'
import { EditMachineQuery } from '@/screencheck_portal_api/src'
import { machineFileWithDefaults } from '@/utils/MachineFile'
import { DateTime } from 'luxon'
import OpenReviewsNotification from '@/components/machine_file/OpenReviewsNotification.vue'

export default {
  name: 'MachineFileEditPage',
  components: {
    OpenReviewsNotification,
    staticData,
    weightData,
    applicationData,
    mediaData,
    suspensionData,
    lubricationData,
    motorData,
    revisionData
  },
  data () {
    return {
      machine: null,
      machineFile: null,
      machineFileOriginal: null,
      errorText: null,
      saveErrorText: null,
      isSaving: false
    }
  },
  mounted () {
    this.fetchMachine()
    this.$store.dispatch('loadDealers')
  },
  computed: {
    userHasRole: function () {
      return this.$store.getters.isUserAdmin || this.$store.getters.isUserEngineeringManager
    },
    machineSerial: function () {
      return this.$route.params.serial
    }
  },
  watch: {
    $route: {
      handler: function (to) {
        if (to.name === 'machine-file-edit' && this.$route.params.serial !== undefined) {
          this.fetchMachine()
        }
      }
    }
  },
  methods: {
    fetchMachine: function () {
      this.machine = null
      this.machineFile = null
      this.machineFileOriginal = null
      this.saveErrorText = null
      machinesApi.getMachine(this.machineSerial, (error, data) => {
        if (error) {
          this.errorText = 'Failed fetching machine data'
          console.error('Failed fetching machine because: ' + error)
          return
        }
        this.machine = data
        this.errorText = null
        this.fetchMachineFileDetails()
      })
    },
    fetchMachineFileDetails: function () {
      this.machineFile = null
      this.machineFileOriginal = null
      machinesApi.getMachineFileDetails(this.machineSerial, (error, data) => {
        if (error) {
          this.errorText = 'Failed fetching machine file data'
          console.error('Failed fetching machine file data because: ' + error)
          return
        }
        if (Object.keys(data).length === 0) {
          console.error('No machine file data available')
        }
        this.machineFile = machineFileWithDefaults(this.machineSerial, data)
        this.machineFileOriginal = machineFileWithDefaults(this.machineSerial, data)
        this.errorText = null
      })
    },
    applyChanges: function (machineFile) {
      this.machineFile = machineFile
      this.saveErrorText = null
    },
    saveChanges: function () {
      this.saveErrorText = null
      const rawSerial = this.machineFile?.static?.machineSerialNumber
      const cleanSerial = (typeof rawSerial === 'string' ? rawSerial.trim() : '').toUpperCase()

      if (!cleanSerial) {
        this.saveErrorText = 'Serial number must not be empty.'
        return
      }

      if (/[^A-Z0-9 .-]/.test(cleanSerial)) {
        this.saveErrorText = 'Serial number contains invalid characters. Allowed characters: letters, numbers, spaces, dots, and hyphens.'
        return
      }

      if (this.machineFile?.static) {
        this.machineFile.static.machineSerialNumber = cleanSerial
      }

      if (JSON.stringify(this.machineFileOriginal) !== JSON.stringify(this.machineFile)) {
        this.isSaving = true
        this.machineFile.timestamp = new Date()
        const editMachineQuery = EditMachineQuery.constructFromObject({
          serial: cleanSerial,
          type: this.machineFile.static?.machineType,
          owner: this.machineFile.static?.customerName,
          data: this.machineFile
        })
        editMachineQuery.data.timestamp = DateTime.now().toString()
        machinesApi.editMachine(this.machineSerial, editMachineQuery, (error) => {
          this.isSaving = false
          if (error) {
            this.saveErrorText = error.response?.body?.message || error.message || 'Failed saving machine file data'
            console.error('Failed saving machine file data because: ' + this.saveErrorText)
            return
          }
          this.$refs.successToast.show()
          this.$router.push({ name: 'machine', params: { serial: cleanSerial } }).catch(() => {})
        })
      } else {
        this.$refs.successToast.show()
        this.$router.push({ name: 'machine', params: { serial: cleanSerial } }).catch(() => {})
      }
    }
  }
}
</script>

<style lang="scss">
</style>
